// Issue Bulk Membership Passes — superadmin, club-scoped.
//
// Two distinct steps, and their outputs must not be confused for one another:
//
//   1. VALIDATE  — POST /validatecsv. Synchronous, seconds. Answers "is this
//      file well-formed?" and may return files of rejected rows to download.
//      Its output is `validation`, shown in the dashed panel.
//   2. SUBMIT    — POST /csvBulkSubmit. Returns 202 + a job id and runs for
//      minutes. Its output is the job panels from BulkJobStatus.
//
// `validation` used to hold BOTH — the validate response, the submit response,
// or a bare string — which made the render branch on a mixed shape. Submit now
// lives entirely in useBulkJob, so this state means one thing again.
//
// The job lifecycle (polling, resuming on arrival, attaching to a refused
// duplicate, when the error file may be offered) is shared with the other two
// bulk pages via useBulkJob; this page owns its validate step, chrome and
// payload.
import React, { useEffect, useState, useContext } from "react";
import { Grid, Box } from '@mui/material';
import { Paper, Button, Typography } from '@mui/material';
import { useUrlContext } from '../context/urlContext';
import axios from "axios";
import { errorContext } from "../Components/ErrorDisplay";
import { describeError } from "../Components/errorUtils";
import { useAuth } from '../context/authContext';
import { useHistory } from "react-router-dom";
import useBulkJob from '../Components/useBulkJob';
import BulkJobStatus from '../Components/BulkJobStatus';
import { JOB_PROCESS } from '../Components/useJobPoller';

export default function BulkPassCreation() {
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const history = useHistory();
    const { handleError, setErrorMessage, notify } = useContext(errorContext);

    const [csvFile, setCsvFile] = useState(null);
    // Validation output only — never the job's. See the header note.
    const [validation, setValidation] = useState("");
    const [validateClicked, setValidateClicked] = useState(false);
    const [validated, setValidated] = useState(false);

    const job = useBulkJob({
        processCode: JOB_PROCESS.BULK_PASS,
        fallbackErrorMessage: 'There were errors creating passes.',
    });
    const { submitting, submitJob, resetJob } = job;

    useEffect(() => {
        setErrorMessage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Submit needs a validated file AND no job in flight. `submitting` covers
    // both the upload and the whole polling run — including a job adopted on
    // arrival that this tab never started.
    const isSubmitEnabled = validated && csvFile !== null && !submitting;
    // Validating a file you cannot submit is a dead end, so this is locked too.
    const isValidateEnabled = csvFile !== null && !submitting;

    async function handleValidateCSV() {
        const data = new FormData();
        data.append("image", csvFile);
        data.append("filename", csvFile.name);
        await axios.post(UrlHelperObj.validateCsvUrl(), data, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(response => response.data)
            .then(results => {
                setValidation(results);
                setValidateClicked(true);
                setValidated(true);
                // The server's message (valid / has-errors) is shown by the
                // global response interceptor.
            })
            .catch(err => {
                // Keep the structured response (with any error files) for the
                // panel when present; otherwise a clear classified message.
                setValidation(err?.response?.data || describeError(err));
                setValidateClicked(true);
                setValidated(false);
                notify(describeError(err), 'error');
            });
    }

    async function handleSubmit() {
        const data = new FormData();
        // NOTE: this endpoint takes `clubid`, not the `issuer_id` the other two
        // bulk endpoints use. Same value, different field name — don't "align".
        data.append("clubid", user.issuerId);
        data.append("image", csvFile);

        const chosenName = csvFile.name;
        // Clearing the file, and the validation that gated it, is what stops a
        // second click re-running the same import.
        setCsvFile(null);
        setValidated(false);
        // The validation notice has served its purpose the moment the import
        // starts — leaving "CSV validation successful, please continue" sitting
        // above the job panel says nothing useful, and it would render red now
        // that `validated` is false. Only ever a SUCCESS notice is cleared here:
        // Submit is enabled solely when validation passed, so a failure notice
        // can never reach this point.
        setValidation("");
        setValidateClicked(false);

        await submitJob(UrlHelperObj.csvBulkSubmitUrl(), data, chosenName);

        const fileInput = document.getElementById("csv-upload");
        if (fileInput) fileInput.value = "";
    }

    // Downloads a file of rejected rows named by the VALIDATION response. This
    // is not the job's error file — that one is handled inside useBulkJob.
    async function downloadFile(fileName) {
        try {
            const response = await axios.get(UrlHelperObj.downloadCsvUrl(fileName), {
                responseType: 'text'
            });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
            const link = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            handleError(error);
        }
    }

    function handleChangeInFile(e) {
        setValidation("");
        setValidated(false);
        setValidateClicked(false);
        resetJob();
        const file = e.target.files[0];
        setCsvFile(file || null);
    }

    return (
        <Grid container xs={12} spacing={0} style={{ borderStyle: 'none', borderColor: 'blue', justifyContent: 'flex-start', marginLeft: 10 }}>
            <Grid container item xs={12} style={{ textAlign: 'left', borderStyle: 'none', borderColor: 'greeen' }}>
                <Grid item xs={12}>
                    <Paper style={{ padding: '8px 12px' }}>
                        <Typography variant="h5" style={{ textAlign: 'left' }}>Issue Membership Passes in Bulk</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Instructions */}
            <Grid container xs={12} direction="column" sx={{ mb: 1, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '20px', width: '100%', maxWidth: '700px',
                    borderStyle: 'none', borderColor: 'green', mb: 2, padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff'
                }}>
                    <Typography>Please select CSV file for issuing bulk membership passes</Typography>
                </Grid>
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 0, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '10px', width: '100%', maxWidth: '700px',
                    borderStyle: 'none', borderColor: 'green', mb: 0, padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff'
                }}>
                    {/* Disabled while a job is in flight. The `disabled` on the
                        INPUT is what actually stops it: a label click on a
                        disabled control does not open the picker. */}
                    <input
                        id="csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleChangeInFile}
                        disabled={submitting}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="csv-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            disabled={submitting}
                            sx={{ minWidth: 160, height: 26 }}
                        >
                            Choose CSV File
                        </Button>
                    </label>
                    <Typography style={{ marginTop: '8px', textAlign: 'left' }}>
                        <span style={{ fontWeight: 700 }}>Expected CSV headers:</span>
                        <br />
                        number, cardno, lastname, firstname, cardexpiry, mobile, email
                    </Typography>
                </Grid>
            </Grid>

            <Grid container item xs={12} sx={{ borderStyle: 'none', borderColor: 'black', direction: "column" }}>
                <Grid item xs={12} style={{
                    textAlign: 'left', marginTop: '10px',
                    borderStyle: 'none', borderColor: 'green', mb: 0, padding: '5px',
                    borderRadius: '5px', backgroundColor: '#f0f8ff'
                }}>
                    <Box sx={{
                        borderStyle: 'none', borderColor: 'green',
                        display: 'flex', gap: 10, alignItems: 'flex-start', maxWidth: 800,
                    }}>
                        <Box sx={{ flex: 1, maxWidth: 500 }}>
                            <Typography
                                sx={{
                                    border: '1px dashed #ccc', borderRadius: 1, p: 1, ml: '5px',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'flex-start', textAlign: 'left',
                                }}
                                color="text.secondary"
                            >
                                {/* After submit csvFile is null, so fall back to
                                    the name the job reports — which is also how a
                                    resumed job names its file. */}
                                {csvFile ? csvFile.name : (job.submittedFileName || 'No file selected')}
                            </Typography>
                        </Box>

                        {/* Validate */}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleValidateCSV}
                            sx={{ minWidth: 140, flexShrink: 0 }}
                            disabled={!isValidateEnabled}
                        >
                            Validate CSV
                        </Button>
                    </Box>
                </Grid>

                {/* VALIDATION output — deliberately separate from the job panels
                    below, which answer a different question. */}
                {validateClicked === true ? (
                    <Grid item xs={12} style={{
                        textAlign: 'left', marginTop: '0px',
                        borderStyle: 'none', borderColor: 'green', mb: 0, padding: '5px',
                        borderRadius: '5px', backgroundColor: '#f0f8ff'
                    }}>
                        <Box sx={{ flex: 1, maxWidth: 800, textAlign: 'left' }}>
                            <Typography
                                sx={{
                                    border: '1px dashed #ccc', borderRadius: 1, p: 1,
                                    minHeight: 36, display: 'flex', alignItems: 'center',
                                    justifyContent: 'flex-start', textAlign: 'left',
                                }}
                                color={validated ? "text.secondary" : "red"}
                            >
                                {validation && validation.error === 0 ? validation.message : ''}
                            </Typography>

                            {validation && validation.files && validation.files.length > 0 ? (
                                <Box sx={{ mt: 0, width: '100%' }}>
                                    <Typography variant="body2" gutterBottom style={{ textAlign: 'left' }}>
                                        Download Files:
                                    </Typography>
                                    {validation.files.map((filename, index) => (
                                        <Box sx={{ width: '100%', mb: 1, border: '1px dashed #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} key={index}>
                                            <Typography variant="body2">{filename}</Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => downloadFile(filename)}
                                            >
                                                Download
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    </Grid>
                ) : null}

                {/* JOB output — progress while the import runs, then its outcome. */}
                <BulkJobStatus {...job} onDownloadErrorFile={job.downloadErrorFile} />
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 0, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '10px', width: '100%', maxWidth: '700px',
                    borderStyle: 'none', borderColor: 'green', mb: 0, padding: '10px', borderRadius: '5px'
                }}>
                    {/* Validation failure text. Job failures render in the job
                        panel above, not here. */}
                    {(typeof validation === 'string' && validation !== "") || (validation && validation.error === 1) ? (
                        <Typography style={{ color: 'red', marginBottom: '8px', marginLeft: '-8px', textAlign: 'left' }}>
                            {typeof validation === 'string' ? validation : validation.message}
                        </Typography>
                    ) : null}
                    <Button type="submit" disabled={!isSubmitEnabled}
                        variant="contained"
                        component="span"
                        onClick={handleSubmit}
                        style={{
                            marginLeft: '-8px',
                            backgroundColor: !isSubmitEnabled ? '#bdbdbd' : 'blue',
                            color: !isSubmitEnabled ? '#616161' : '#ffffff',
                            opacity: 1,
                            boxShadow: !isSubmitEnabled ? 'none' : undefined,
                            cursor: !isSubmitEnabled ? 'not-allowed' : undefined
                        }}
                        sx={{
                            '&.Mui-disabled': {
                                backgroundColor: '#bdbdbd !important',
                                color: '#616161 !important',
                                opacity: 1
                            }
                        }}
                    >
                        {submitting ? (job.isPolling ? 'Processing…' : 'Submitting…') : 'Submit'}
                    </Button>
                    {/* "Back", not "Cancel": this only navigates — it has never
                        stopped anything, and during a running import "Cancel"
                        would read as "abort this import". */}
                    <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        onClick={() => history.push(user?.issuerId ? `/club/${user.issuerId}/home` : "/")}
                        style={{ marginLeft: '8px' }}
                    >
                        Back
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}
